/**
 * Settings > Insurance plans & carriers (owner feedback 2026-10-19: "I don't
 * see how to edit insurance plans"). Routes under `/v1/settings/insurance-plans`
 * and `/v1/settings/carriers`.
 *
 * One page for the practice's plans: carrier, group, plan type, network,
 * fee schedule, and the benefits estimates use (coverage % by category,
 * deductible, annual maximum, frequency limits).
 *
 * Write-back: none. No PMS gateway command carries insurance plans, so a
 * plan or carrier that came from the PMS (origin `pms`) is read-only here
 * and the page says "Edit this in <PMS>". Velora-owned plans are edited in
 * Velora. Nothing on this page ever writes to a PMS.
 */
import { z } from 'zod';
import { IdSchema } from './sync.js';

export const INSURANCE_PLAN_PERMISSIONS = {
  view: 'insurance.view',
  edit: 'insurance.edit',
} as const;

export const BENEFIT_CATEGORY_KEYS = [
  'diagnostic',
  'preventive',
  'basic',
  'endodontics',
  'periodontics',
  'oral_surgery',
  'major',
  'prosthodontics',
  'implants',
  'orthodontics',
] as const;
export const BenefitCategoryKeySchema = z.enum(BENEFIT_CATEGORY_KEYS);
export type BenefitCategoryKey = z.infer<typeof BenefitCategoryKeySchema>;

export const PlanTypeKeySchema = z.enum([
  'ppo',
  'premier',
  'hmo',
  'capitation',
  'medicaid',
  'indemnity',
  'discount',
  'other',
]);
export const NetworkSchema = z.enum(['in', 'out', 'unknown']);

const Cents = z.number().int().min(0).max(100_000_000);
const Code = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^D[0-9]{4}$/, 'A CDT code is D and four digits.');

export const FrequencyPeriodKeySchema = z.enum([
  'calendar_year',
  'benefit_year',
  'lifetime',
  'months',
]);

export const PlanFrequencySchema = z.object({
  /** The codes that share the limit (D1110, D1120, ...). */
  codes: z.array(Code).min(1).max(12),
  count: z.number().int().min(1).max(99),
  period: FrequencyPeriodKeySchema,
  /** For `months`: the rolling window (36 = once every 3 years). */
  months: z.number().int().min(1).max(240).nullable(),
  scope: z.enum(['patient', 'tooth', 'quadrant']),
});
export type PlanFrequency = z.infer<typeof PlanFrequencySchema>;

export const PlanBenefitsSchema = z.object({
  /** Insurance pays this percent; null = not on file (never 0 by default). */
  coverage: z.array(
    z.object({
      category: BenefitCategoryKeySchema,
      percent: z.number().int().min(0).max(100).nullable(),
    }),
  ),
  deductibleCents: Cents.nullable(),
  familyDeductibleCents: Cents.nullable(),
  annualMaxCents: Cents.nullable(),
  frequencies: z.array(PlanFrequencySchema),
});
export type PlanBenefits = z.infer<typeof PlanBenefitsSchema>;

export const InsurancePlanSummarySchema = z.object({
  id: IdSchema,
  carrier: z.object({
    id: IdSchema,
    name: z.string(),
    payerId: z.string().nullable(),
    phone: z.string().nullable(),
  }),
  groupName: z.string().nullable(),
  groupNumber: z.string().nullable(),
  planType: PlanTypeKeySchema,
  network: NetworkSchema,
  feeSchedule: z.object({ id: IdSchema, name: z.string() }).nullable(),
  annualMaxCents: Cents.nullable(),
  deductibleCents: Cents.nullable(),
  /** Patients with a current coverage on the plan. */
  patients: z.number().int().min(0),
  isHidden: z.boolean(),
  origin: z.enum(['velora', 'pms']),
  /** "Dentrix", "Open Dental" when the plan came from a PMS. */
  pmsName: z.string().nullable(),
  /** Why this plan cannot be edited here; null when it can. */
  lockedReason: z.string().nullable(),
});
export type InsurancePlanSummary = z.infer<typeof InsurancePlanSummarySchema>;

export const InsurancePlanListQuerySchema = z.object({
  q: z.string().trim().max(100).default(''),
  carrierId: IdSchema.optional(),
  hidden: z.enum(['true', 'false']).default('false'),
});

export const InsurancePlanListSchema = z.object({
  plans: z.array(InsurancePlanSummarySchema),
  /** More plans match than were returned (search to narrow). */
  truncated: z.boolean(),
  feeSchedules: z.array(z.object({ id: IdSchema, name: z.string(), kind: z.string() })),
  canEdit: z.boolean(),
  /** The plain-words write-back rule shown on the page. */
  writeBack: z.string(),
});
export type InsurancePlanList = z.infer<typeof InsurancePlanListSchema>;

export const InsurancePlanDetailSchema = InsurancePlanSummarySchema.extend({
  benefitYearStartMonth: z.number().int().min(1).max(12),
  benefits: PlanBenefitsSchema,
  /**
   * Where `benefits.coverage` came from: `categories` (entered in Velora or
   * from eligibility), `pms_table` (the PMS coverage table, shown as the
   * percent estimates apply to each category), `none` (no coverage on file:
   * every estimate on this plan is $0 until it is entered or synced).
   */
  coverageSource: z.enum(['categories', 'pms_table', 'none']).optional(),
  /** The PMS coverage table as synced (code range or category, percent). */
  pmsCoverage: z
    .array(
      z.object({
        label: z.string(),
        percent: z.number().int().min(0).max(100).nullable(),
        /** False when Velora cannot tell which codes the label covers. */
        mapped: z.boolean(),
      }),
    )
    .optional(),
  /** PMS categories with no deductible ($0 category deductible rows). */
  deductibleWaived: z.array(z.string()).optional(),
  version: z.number().int(),
});
export type InsurancePlanDetail = z.infer<typeof InsurancePlanDetailSchema>;

export const InsurancePlanPatchSchema = z
  .object({
    expectedVersion: z.number().int(),
    groupName: z.string().trim().max(200).nullable().optional(),
    groupNumber: z.string().trim().max(60).nullable().optional(),
    planType: PlanTypeKeySchema.optional(),
    network: NetworkSchema.optional(),
    feeScheduleId: IdSchema.nullable().optional(),
    benefitYearStartMonth: z.number().int().min(1).max(12).optional(),
    isHidden: z.boolean().optional(),
    benefits: PlanBenefitsSchema.optional(),
  })
  .strict();
export type InsurancePlanPatch = z.input<typeof InsurancePlanPatchSchema>;

export const InsurancePlanCreateSchema = z
  .object({
    carrierId: IdSchema.nullable().default(null),
    carrierName: z.string().trim().max(200).nullable().default(null),
    payerId: z
      .string()
      .trim()
      .max(20)
      .regex(/^[0-9A-Za-z-]*$/, 'A payer id is letters, digits and dashes.')
      .nullable()
      .default(null),
    groupName: z.string().trim().max(200).nullable().default(null),
    groupNumber: z.string().trim().max(60).nullable().default(null),
    planType: PlanTypeKeySchema.default('ppo'),
  })
  .strict()
  .refine((p) => p.carrierId !== null || (p.carrierName ?? '') !== '', 'Pick or name a carrier.');
export type InsurancePlanCreate = z.input<typeof InsurancePlanCreateSchema>;

export const CarrierPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    payerId: z
      .string()
      .trim()
      .max(20)
      .regex(/^[0-9A-Za-z-]*$/, 'A payer id is letters, digits and dashes.')
      .nullable()
      .optional(),
    phone: z.string().trim().max(40).nullable().optional(),
  })
  .strict();
export type CarrierPatch = z.input<typeof CarrierPatchSchema>;

export const CarrierSchema = z.object({
  id: IdSchema,
  name: z.string(),
  payerId: z.string().nullable(),
  phone: z.string().nullable(),
  plans: z.number().int().min(0),
  origin: z.enum(['velora', 'pms']),
  lockedReason: z.string().nullable(),
});
export type Carrier = z.infer<typeof CarrierSchema>;

/** The sentence the page shows for a plan kept in a PMS. */
export function pmsPlanLockedReason(pms: string | null): string {
  const name = pms ?? 'the practice software';
  return `This plan comes from ${name}. Edit it in ${name}: Velora does not write insurance plans to ${name}, and the change shows here after the next sync.`;
}
